import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const Products = () => {
  const products = [
    {
      id: 'digimitra',
      name: 'DigiMitra',
      tagline: 'Your Digital Friend at Your Doorstep',
      description: 'Personalized home visits by trained DigiMitra service providers who help seniors learn to use smartphones, apps, and stay safe online. One-on-one guidance in the comfort of your home.',
      features: [
        'One-on-one training at home',
        'Learn WhatsApp, UPI, video calls',
        'Cyber safety education',
        'Patient, elder-friendly approach'
      ],
      cta: 'Book a Home Visit',
      ctaLink: '/services',
      color: '#e5266a',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      ),
      isExternal: false
    },
    {
      id: 'scamtrack',
      name: 'ScamTrack',
      tagline: 'Report & Track Scams Targeting Seniors',
      description: 'A community-powered platform to report, track, and warn others about scams targeting the elderly. Help protect fellow seniors by sharing your experiences.',
      features: [
        'Report scams anonymously',
        'Browse scam database',
        'Get alerts on new scams',
        'Community-driven protection'
      ],
      cta: 'Visit ScamTrack',
      ctaLink: 'https://scamtrack.elderra.io',
      color: '#ff6b35',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          <path d="M12 8v4"></path>
          <path d="M12 16h.01"></path>
        </svg>
      ),
      isExternal: true
    },
    {
      id: 'voice-companion',
      name: 'Elderra Voice Companion',
      tagline: 'Your Smart Helper for Everyday Services, on WhatsApp',
      description: 'A voice-and-chat companion for seniors, right on WhatsApp. Get help booking appointments, finding information, or navigating digital services - all through simple conversation or a voice note.',
      features: [
        'Simple chat interface',
        'Voice input support',
        'Book services easily',
        'Available 24/7'
      ],
      cta: 'Chat on WhatsApp',
      ctaLink: 'https://wa.me/919205070300?text=Hi%20Elderra%2C%20I%27d%20like%20to%20try%20the%20Voice%20Companion',
      color: '#6366f1',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          <path d="M8 10h.01"></path>
          <path d="M12 10h.01"></path>
          <path d="M16 10h.01"></path>
        </svg>
      ),
      isExternal: true
    }
  ];

  return (
    <div className="products-page">
      <SEO 
        title="Products | Elderra - Digital Solutions for Seniors"
        description="Explore Elderra's suite of products designed to help seniors navigate the digital world safely - DigiMitra home visits, ScamTrack scam reporting, and Elderra Voice Companion on WhatsApp."
      />
      {/* Hero Section */}
      <section className="products-hero">
        <div className="container">
          <h1>Our Products</h1>
          <p className="hero-subtitle">
            A suite of tools designed to empower seniors in the digital age
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="products-section">
        <div className="container">
          <div className="products-grid">
            {products.map((product, index) => (
              <div 
                key={product.id} 
                className="product-card"
                data-testid={`product-${product.id}`}
                style={{'--accent-color': product.color}}
              >
                <div className="product-icon" style={{color: product.color}}>
                  {product.icon}
                </div>
                <h2 className="product-name">{product.name}</h2>
                <p className="product-tagline">{product.tagline}</p>
                <p className="product-description">{product.description}</p>
                <ul className="product-features">
                  {product.features.map((feature, i) => (
                    <li key={i}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={product.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                {product.isExternal ? (
                  <a 
                    href={product.ctaLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="product-cta"
                    style={{backgroundColor: product.color}}
                  >
                    {product.cta}
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </a>
                ) : (
                  <Link 
                    to={product.ctaLink}
                    className="product-cta"
                    style={{backgroundColor: product.color}}
                  >
                    {product.cta}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="products-cta-section">
        <div className="container">
          <h2>Want to Support Our Mission?</h2>
          <p>Help us build more tools to empower India's seniors</p>
          <Link to="/donate">
            <Button className="donate-cta-btn">Donate Now</Button>
          </Link>
        </div>
      </section>

    </div>
  );
};

export default Products;
